/**
 * This code was AUTOGENERATED using the codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  getStructDecoder,
  getStructEncoder,
  getU8Decoder,
  getU8Encoder,
  transformEncoder,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type IAccountMeta,
  type IInstruction,
  type IInstructionWithAccounts,
  type IInstructionWithData,
  type ReadonlyAccount,
} from '@solana/kit';
import {
  getTokenAccountAssertionsDecoder,
  getTokenAccountAssertionsEncoder,
  type TokenAccountAssertions,
  type TokenAccountAssertionsArgs,
} from '../../hooked';
import { LIGHTHOUSE_PROGRAM_ADDRESS } from '../programs';
import { getAccountMetaFactory, type ResolvedAccount } from '../shared';
import {
  LogLevel,
  getLogLevelDecoder,
  getLogLevelEncoder,
  type LogLevelArgs,
} from '../types';

export const ASSERT_TOKEN_ACCOUNT_MULTI_DISCRIMINATOR = 10;

export function getAssertTokenAccountMultiDiscriminatorBytes() {
  return getU8Encoder().encode(ASSERT_TOKEN_ACCOUNT_MULTI_DISCRIMINATOR);
}

export type AssertTokenAccountMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountTargetAccount extends string | IAccountMeta<string> = string,
  TRemainingAccounts extends readonly IAccountMeta<string>[] = [],
> = IInstruction<TProgram> &
  IInstructionWithData<Uint8Array> &
  IInstructionWithAccounts<
    [
      TAccountTargetAccount extends string
        ? ReadonlyAccount<TAccountTargetAccount>
        : TAccountTargetAccount,
      ...TRemainingAccounts,
    ]
  >;

export type AssertTokenAccountMultiInstructionData = {
  discriminator: number;
  logLevel: LogLevel;
  assertions: TokenAccountAssertions;
};

export type AssertTokenAccountMultiInstructionDataArgs = {
  logLevel?: LogLevelArgs;
  assertions: TokenAccountAssertionsArgs;
};

export function getAssertTokenAccountMultiInstructionDataEncoder(): Encoder<AssertTokenAccountMultiInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', getU8Encoder()],
      ['logLevel', getLogLevelEncoder()],
      ['assertions', getTokenAccountAssertionsEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: ASSERT_TOKEN_ACCOUNT_MULTI_DISCRIMINATOR,
      logLevel: value.logLevel ?? LogLevel.Silent,
    })
  );
}

export function getAssertTokenAccountMultiInstructionDataDecoder(): Decoder<AssertTokenAccountMultiInstructionData> {
  return getStructDecoder([
    ['discriminator', getU8Decoder()],
    ['logLevel', getLogLevelDecoder()],
    ['assertions', getTokenAccountAssertionsDecoder()],
  ]);
}

export function getAssertTokenAccountMultiInstructionDataCodec(): Codec<
  AssertTokenAccountMultiInstructionDataArgs,
  AssertTokenAccountMultiInstructionData
> {
  return combineCodec(
    getAssertTokenAccountMultiInstructionDataEncoder(),
    getAssertTokenAccountMultiInstructionDataDecoder()
  );
}

export type AssertTokenAccountMultiInput<
  TAccountTargetAccount extends string = string,
> = {
  /** Target account to be asserted */
  targetAccount: Address<TAccountTargetAccount>;
  logLevel?: AssertTokenAccountMultiInstructionDataArgs['logLevel'];
  assertions: AssertTokenAccountMultiInstructionDataArgs['assertions'];
};

export function getAssertTokenAccountMultiInstruction<
  TAccountTargetAccount extends string,
  TProgramAddress extends Address = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
>(
  input: AssertTokenAccountMultiInput<TAccountTargetAccount>,
  config?: { programAddress?: TProgramAddress }
): AssertTokenAccountMultiInstruction<TProgramAddress, TAccountTargetAccount> {
  // Program address.
  const programAddress = config?.programAddress ?? LIGHTHOUSE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    targetAccount: { value: input.targetAccount ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  const instruction = {
    accounts: [getAccountMeta(accounts.targetAccount)],
    programAddress,
    data: getAssertTokenAccountMultiInstructionDataEncoder().encode(
      args as AssertTokenAccountMultiInstructionDataArgs
    ),
  } as AssertTokenAccountMultiInstruction<
    TProgramAddress,
    TAccountTargetAccount
  >;

  return instruction;
}

export type ParsedAssertTokenAccountMultiInstruction<
  TProgram extends string = typeof LIGHTHOUSE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly IAccountMeta[] = readonly IAccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Target account to be asserted */
    targetAccount: TAccountMetas[0];
  };
  data: AssertTokenAccountMultiInstructionData;
};

export function parseAssertTokenAccountMultiInstruction<
  TProgram extends string,
  TAccountMetas extends readonly IAccountMeta[],
>(
  instruction: IInstruction<TProgram> &
    IInstructionWithAccounts<TAccountMetas> &
    IInstructionWithData<Uint8Array>
): ParsedAssertTokenAccountMultiInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 1) {
    // TODO: Coded error.
    throw new Error('Not enough accounts');
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = instruction.accounts![accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      targetAccount: getNextAccount(),
    },
    data: getAssertTokenAccountMultiInstructionDataDecoder().decode(
      instruction.data
    ),
  };
}
